import { useDispatch } from 'react-redux';
import { addTab } from '../store/slices/TabsSlice';

export const useTabNavigation = () => {
  const dispatch = useDispatch();

  const openInTab = (config) => {
    const { path, title, id, type = 'page', data } = config;
    
    const tabId = id || path;

    dispatch(addTab({
      id: tabId,
      title,
      path,
      type,
      data,
    }));

    // Don't navigate - tabs handle their own routing internally
  };

  const openClient = (clientId, clientName) => {
    openInTab({
      id: `client-${clientId}`,
      title: clientName || `Client ${clientId}`,
      path: `/client/${clientId}`,
      type: 'client',
      data: { clientId, clientName },
    });
  };

  // Like openClient but lands on a specific accordion tab index (e.g. 4 = Calendar).
  // Optionally scroll to + highlight a specific field on arrival (focusField, e.g.
  // 'levelOfCareId' — the client page looks for an element id `field-<focusField>`).
  const openClientAtTab = (clientId, clientName, defaultTab, focusField = null) => {
    openInTab({
      id: `client-${clientId}`,
      title: clientName || `Client ${clientId}`,
      path: `/client/${clientId}`,
      type: 'client',
      data: { clientId, clientName, defaultTab, focusField },
    });
  };

  const openService = (serviceId, serviceName, clientId, clientServiceId = 0, formVersion = null) => {
    openInTab({
      id: `service-${clientId}-${serviceId}-${clientServiceId}`,
      title: serviceName || `Service ${serviceId}`,
      path: `/psychosocialAssessment/${clientId}/${serviceId}/${clientServiceId}`,
      type: 'service',
      data: { serviceId, serviceName, clientId, clientServiceId, formVersion },
    });
  };

  // Group (multi-client) session form: same assessment page, opened in its own tab. `groupTargets`
  // ([{clientId, clientServiceId}]) rides in tab data so the form saves once to every client in the
  // session (PsychosocialAssessment reads it from useTabParams). Distinct id prefix so it doesn't
  // collide with the single-client tab for the same first client.
  const openGroupService = (serviceId, serviceName, clientId, clientServiceId = 0, formVersion = null, groupTargets = null) => {
    openInTab({
      id: `group-service-${serviceId}-${clientServiceId}`,
      title: serviceName || `Session ${serviceId}`,
      path: `/psychosocialAssessment/${clientId}/${serviceId}/${clientServiceId}`,
      type: 'service',
      data: { serviceId, serviceName, clientId, clientServiceId, formVersion, groupTargets },
    });
  };

  const openPage = (path, title) => {
    openInTab({
      id: path,
      title,
      path,
      type: 'page',
    });
  };

  // Open the full calendar page filtered to one client (its own tab), navigated to the week of
  // targetDate. groupBy optionally sets the calendar grouping ('services' | 'providers' | 'clients').
  // navKey forces the calendar to re-navigate/re-group even when reopened with the same params.
  const openCalendarForClient = (clientId, clientName, targetDate = null, groupBy = null) => {
    openInTab({
      id: `calendar-client-${clientId}`,
      title: clientName ? `${clientName} · Calendar` : 'Calendar',
      path: '/calendar',
      type: 'page',
      data: { lockedClientId: clientId, clientName, targetDate, groupBy, navKey: Date.now() },
    });
  };

  return {
    openInTab,
    openClient,
    openClientAtTab,
    openService,
    openGroupService,
    openPage,
    openCalendarForClient,
  };
};
